import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMenu, FiX, FiShoppingCart, FiUser, FiSearch } from 'react-icons/fi';
import { useCart } from '@hooks/useCart';
import { useAuth } from '@hooks/useAuth';
import LoginModal from '@components/common/LoginModal';
import QuoteModal from '@components/common/QuoteModal';

/**
 * Header Component
 * Props:
 * - links: [{title, href, sublinks?}]
 * - logoUrl
 * - cartCount
 * - userLoggedIn
 * Features:
 * - Responsive hamburger menu on mobile
 * - Dropdowns for categories/subcategories
 * - Search bar with autocomplete
 * - Cart badge dynamically updated
 * - Login/Logout toggle for B2C/B2B
 * - Animations: dropdown fade-in/out, hover scale, sticky on scroll
 * - Accessibility: keyboard navigation, aria labels
 */

const Header = ({ logoUrl = '/logo.svg' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  
  const { cartItems } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const cartCount = cartItems?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  const links = [
    {
      title: 'Products',
      href: '/products',
      sublinks: [
        { title: 'Security Cameras', href: '/products?category=cameras' },
        { title: 'Alarm Systems', href: '/products?category=alarms' },
        { title: 'Access Control', href: '/products?category=access-control' },
        { title: 'Smart Locks', href: '/products?category=smart-locks' },
      ],
    },
    {
      title: 'Services',
      href: '/services',
      sublinks: [
        { title: 'Installation', href: '/services?type=installation' },
        { title: 'Maintenance', href: '/services?type=maintenance' },
        { title: 'Consultation', href: '/services?type=consultation' },
      ],
    },
    { title: 'B2B Portal', href: '/b2b' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-white border-b border-gray-200'
      }`}
    >
      <nav className="container-custom py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-800 rounded flex items-center justify-center">
              <span className="text-white font-bold text-xl">GK</span>
            </div>
            <span className="text-2xl font-bold text-primary-800 tracking-tight">SecureGuard</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            {links.map((link) => (
              <div
                key={link.title}
                className="relative"
                onMouseEnter={() => link.sublinks && setActiveDropdown(link.title)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.href}
                  className="text-gray-700 hover:text-primary-800 font-medium transition-colors duration-300 uppercase text-sm tracking-wide"
                  aria-haspopup={link.sublinks ? 'true' : 'false'}
                >
                  {link.title}
                </Link>

                {/* Dropdown */}
                {link.sublinks && (
                  <AnimatePresence>
                    {activeDropdown === link.title && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 mt-2 w-48 bg-white shadow-lg rounded-lg overflow-hidden"
                      >
                        {link.sublinks.map((sublink) => (
                          <Link
                            key={sublink.title}
                            to={sublink.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-secondary-50 hover:text-secondary-600 transition-colors duration-200"
                          >
                            {sublink.title}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </div>

          {/* Get Quote Button */}
          <div className="hidden lg:block">
            <button
              onClick={() => setShowQuoteModal(true)}
              className="btn-primary px-6 py-2.5 text-sm"
            >
              Get a Quote
            </button>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="hidden lg:flex items-center">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-400 w-64"
                aria-label="Search products"
              />
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </form>

          {/* User Actions */}
          <div className="flex items-center space-x-4">
            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-2 hover:scale-110 transition-transform duration-300"
              aria-label={`Shopping cart with ${cartCount} items`}
            >
              <FiShoppingCart className="text-2xl text-gray-700" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-secondary-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User Account */}
            {user ? (
              <div className="hidden lg:flex items-center space-x-2">
                <FiUser className="text-xl text-gray-700" />
                <button
                  onClick={logout}
                  className="text-sm text-gray-700 hover:text-primary-800 transition-colors duration-300 uppercase tracking-wide"
                >
                  Logout
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowLoginModal(true)}
                className="hidden lg:flex items-center space-x-2 text-gray-700 hover:text-primary-800 transition-colors duration-300"
              >
                <FiUser className="text-xl" />
                <span className="text-sm uppercase tracking-wide">Login</span>
              </button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <FiX className="text-2xl text-gray-700" />
              ) : (
                <FiMenu className="text-2xl text-gray-700" />
              )}
            </button>
          </div>
        </div>

          {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden mt-4 pb-4 space-y-4"
            >
              {/* Mobile Get Quote Button */}
              <button
                onClick={() => {
                  setShowQuoteModal(true);
                  setIsMenuOpen(false);
                }}
                className="btn-primary w-full"
              >
                Get a Quote
              </button>

              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary-400"
                />
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              </form>

              {/* Mobile Links */}
              {links.map((link) => (
                <div key={link.title}>
                  <Link
                    to={link.href}
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-2 text-gray-700 hover:text-secondary-500 font-medium"
                  >
                    {link.title}
                  </Link>
                  {link.sublinks && (
                    <div className="pl-4 space-y-2 mt-2">
                      {link.sublinks.map((sublink) => (
                        <Link
                          key={sublink.title}
                          to={sublink.href}
                          onClick={() => setIsMenuOpen(false)}
                          className="block py-1 text-sm text-gray-600 hover:text-secondary-500"
                        >
                          {sublink.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {/* Mobile User */}
              {user ? (
                <button
                  onClick={() => {
                    logout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-gray-700 hover:text-secondary-500 font-medium"
                >
                  Logout
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowLoginModal(true);
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-gray-700 hover:text-secondary-500 font-medium"
                >
                  Login
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />

      {/* Quote Modal */}
      <QuoteModal
        isOpen={showQuoteModal}
        onClose={() => setShowQuoteModal(false)}
      />
    </header>
  );
};

export default Header;

